<template>
  <v-form
    id="pop-up"
    @submit.prevent="onSave"
  >
    <v-app-bar
      class="white pa-0"
      elevate-on-scroll
      dense
      scroll-target="#scrolling-alert-edit"
      extension-height="40"
      style="z-index: 3;"
    >
      <h2 class="px-6 text-h5">
        {{ $t('alert.bulk_action.header') }}
      </h2>
      <v-spacer />
      <v-btn
        @click="onSave"
        outlined
        color="primary"
        class="mr-3"
        :data-cy="`${dataCy}_save_btn`"
      >
        {{ $t('alert.details.buttons.save') }}
      </v-btn>
      <v-btn
        @click.prevent="() => {
          clearForm()
          $emit('onClose')
        }"
        fab
        color="transparent"
        class="mr-3"
        small
        depressed
      >
        <v-icon medium>
          mdi-close
        </v-icon>
      </v-btn>
    </v-app-bar>
    <v-container
      fluid
      style="max-height: calc(90vh - 90px);"
      id="scrolling-alert-edit"
      class="overflow-y-auto"
      ref="alert_details_container"
    >
      <v-card flat>
        <v-card-title>{{ $t('alert.details.title') }}</v-card-title>
        <v-card-text>
          <div data-cy="hit_status">
            <v-select
              :label="$t('alert.details.form.hit_status.label')"
              :disabled="is_locked"
              :items="hit_status_options"
              v-model="form.status"
            />
          </div>
          <div data-cy="exposition_menu">
            <v-select
              :label="$t('alert.details.form.exposition.label')"
              :value-comparator="(a, b) => parseInt(a) == parseInt(b)"
              :disabled="is_locked"
              :error="$v.form.exposition.$error"
              :error-messages="$v.form.exposition.$error ? $t('validate.required', { field: $t('alert.details.form.exposition.label') }) : ''"
              :items="exposition_options(form.status)"
              v-model="form.exposition"
            >
              <template
                v-slot:selection="items"
              >
                <div
                  data-cy="selected_exposition"
                >
                  {{ items.item.text }}
                </div>
              </template>
              <template
                v-slot:item="items"
              >
                <div
                  :data-cy="`exposition_${items.item.value}`"
                >
                  {{ items.item.text }}
                </div>
              </template>
            </v-select>
          </div>
          <br>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                :disabled="is_locked"
                :data-cy="`${dataCy}_suggestions_btn`"
              >
                {{ $t('alert.details.buttons.suggestions') }}
              </v-btn>
            </template>
            <v-list>
              <v-list-item
                v-for="(remarks, key) in suggested_remarks"
                @click="form.remarks += (form.remarks.length ? '\n' : '') + remarks"
                :key="key"
              >
                <v-list-item-title
                  :data-cy="`suggestion_${key}`"
                >
                  {{ remarks }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-textarea
            :label="$t('alert.details.form.remarks.label')"
            counter
            auto-grow
            rows="1"
            row-height="15"
            v-model="form.remarks"
            :disabled="is_locked"
            :error="$v.form.remarks.$error"
            :error-messages="$v.form.remarks.$error ? $t('validate.required', { field: $t('alert.details.form.remarks.label') }) : ''"
            :data-cy="`${dataCy}_bulk_action_remark`"
          />
        </v-card-text>
      </v-card>
    </v-container>
    <v-overlay
      :value="requestLoading"
      absolute
      z-index="10"
    >
      <div class="text-center">
        <v-progress-circular
          v-if="!updateSuccess"
          indeterminate
          size="70"
          color="primary"
        />
        <v-icon
          v-else
          size="70"
          color="success"
          class="animate-scale-up"
        >
          mdi-check-circle
        </v-icon>

        <div class="mt-2 text-subtitle-1 font-weight-medium">
          {{ updateSuccess ? $t('alert.bulk_action.msg.success') : $t('alert.bulk_action.msg.process') }}
        </div>
      </div>
    </v-overlay>
  </v-form>
</template>
<script type="text/javascript">
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'

export default {
  name: 'PopupAlertCaseEntityBulkAction',
  props: {
    records: {
      type: Array,
      required: true
    },
    dataCy: {
      type: String,
      default: 'popup_alert_case_entity_bulk_action'
    }
  },
  data () {
    return {
      form: {
        exposition: '',
        remarks: '',
        status: ''
      },
      requestLoading: false,
      updateSuccess: false,
      is_locked: false // Set this to true if the form should be read-only
    }
  },
  validations () {
    return {
      form: {
        status: {
          required
        },
        exposition: {
          required: requiredIf(() => this.form.status === 1 || this.form.status === 2)
        },
        remarks: {
          required: requiredIf(() => this.form.status === 1 || this.form.status === 2),
          maxLength: maxLength(255)
        }
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler () {
        this.$nextTick(() => {
          for (let i = 0; i < this.exposition_options(this.form.status).length; i++) {
            if (String(this.form.exposition) === String(this.exposition_options(this.form.status)[i].value)) {
              return
            }
          }
          // If the current exposition value is not valid for the selected status, reset it
          this.form.exposition = ''
        })
      }
    }
  },
  computed: {
    ...mapGetters('alerts', [
      'hit_status_options',
      'suggested_remarks',
      'exposition_options'
    ])
  },
  methods: {
    async dispatchWithRetry (actionName, payload, maxRetries = 2) {
      let attempts = 0

      while (attempts <= maxRetries) {
        try {
          return await this.$store.dispatch(actionName, payload)
        } catch (error) {
          attempts++

          // If out of retries, throw the error to be caught by the caller
          if (attempts > maxRetries) {
            throw error
          }

          // Wait exponentially before retrying (backoff timing)
          await new Promise(resolve => setTimeout(resolve, attempts * 1000))
        }
      }
    },
    async onSave () {
      this.$v.form.$touch()

      if (this.$v.form.$invalid) {
        return this.$notify({
          type: 'error',
          title: this.$t('alert.details.msg.valid.title'),
          text: this.$t('alert.details.msg.valid.invalid')
        })
      }

      this.requestLoading = true
      this.updateSuccess = false

      try {
        // Process each item one by one
        for (const item of this.records) {
          const payload = {
            ...item,
            ...this.form
          }

          // Execution pauses here until this specific item completes (or fails N-times)
          await this.dispatchWithRetry('alert_case/update', payload, 3)
        }

        this.updateSuccess = true

        // Hold so the user can see the checkmark
        await new Promise(resolve => setTimeout(resolve, 2000))

        this.$notify({
          type: 'success',
          title: this.$t('alert.details.msg.save.title'),
          text: this.$t('alert.details.msg.save.success')
        })

        // Emit success event to parent component to trigger any necessary refresh or state updates
        this.$emit('onSuccess')
        this.clearForm()
      } catch {
        this.$notify({
          type: 'error',
          title: this.$t('alert.details.msg.save.title'),
          text: this.$t('alert.details.msg.save.error')
        })

        // Emit failure event to parent component if needed (optional)
        this.$emit('onFailure')
      } finally {
        this.requestLoading = false
        this.updateSuccess = false
      }
    },
    clearForm () {
      // Reset individual fields back to their default empty values
      this.form = {
        exposition: '',
        remarks: '',
        status: ''
      }

      // Reset VUE validation states
      if (this.$v) {
        this.$v.$reset()
      }
    }
  }
}
</script>

<style scoped>
::v-deep .v-select__selections {
  padding: 4px 0;
}
::v-deep .v-select__selections input {
  display: none;
}
.animate-scale-up {
  animation: scaleUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}
@keyframes scaleUp {
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
</style>
